import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Typography
} from '@mui/material';

const CreatePromoCodeDialog = ({ open, onClose, onCreate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [organization, setOrganization] = useState('EPL');
  const [promoType, setPromoType] = useState('');
  
  // Type-specific fields
  const [percentDiscount, setPercentDiscount] = useState('');
  const [fixedPricePerUnit, setFixedPricePerUnit] = useState('');
  const [buyQuantity, setBuyQuantity] = useState('');
  const [getQuantity, setGetQuantity] = useState('');
  const [getDiscountPercent, setGetDiscountPercent] = useState('');
  
  // Usage limits
  const [usageLimitPerCustomer, setUsageLimitPerCustomer] = useState('');
  const [usageLimitTotal, setUsageLimitTotal] = useState('');
  
  // Minimum requirements
  const [minimumOrderQuantity, setMinimumOrderQuantity] = useState('');
  const [minimumOrderValue, setMinimumOrderValue] = useState('');
  
  // Validity period
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  
  // Internal notes
  const [internalNotes, setInternalNotes] = useState('');
  
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate required fields
      if (!code || !organization || !promoType) {
        setError('Please fill in all required fields');
        return;
      }
      
      // Validate type-specific fields
      if (promoType === 'PERCENTAGE_DISCOUNT' && !percentDiscount) {
        setError('Discount percentage is required for this promo type');
        return;
      }
      
      if (promoType === 'FIXED_PRICE' && !fixedPricePerUnit) {
        setError('Fixed price per unit is required for this promo type');
        return;
      }
      
      if ((promoType === 'BUY_X_GET_Y_FREE' || promoType === 'BUY_X_GET_Y_DISCOUNTED') && (!buyQuantity || !getQuantity)) {
        setError('Buy and get quantities are required for this promo type');
        return;
      }
      
      if (promoType === 'BUY_X_GET_Y_DISCOUNTED' && !getDiscountPercent) {
        setError('Discount percentage for additional items is required for this promo type');
        return;
      }
      
      // Build data object
      const data = {
        code: code.toUpperCase(),
        description: description || undefined,
        organization,
        promoType,
        percentDiscount: percentDiscount ? parseFloat(percentDiscount) : undefined,
        fixedPricePerUnit: fixedPricePerUnit ? parseFloat(fixedPricePerUnit) : undefined,
        buyQuantity: buyQuantity ? parseInt(buyQuantity) : undefined,
        getQuantity: getQuantity ? parseInt(getQuantity) : undefined,
        getDiscountPercent: getDiscountPercent ? parseFloat(getDiscountPercent) : undefined,
        usageLimitPerCustomer: usageLimitPerCustomer ? parseInt(usageLimitPerCustomer) : undefined,
        usageLimitTotal: usageLimitTotal ? parseInt(usageLimitTotal) : undefined,
        minimumOrderQuantity: minimumOrderQuantity ? parseInt(minimumOrderQuantity) : undefined,
        minimumOrderValue: minimumOrderValue ? parseFloat(minimumOrderValue) : undefined,
        // Ensure validFrom is in ISO format if present
        validFrom: validFrom ? new Date(validFrom).toISOString() : undefined,
        // Ensure validUntil is in ISO format if present
        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
        internalNotes: internalNotes || undefined
      };
      
      await onCreate(data);
      
      // Reset form
      resetForm();
    } catch (err) {
      console.error('Error creating promo code:', err);
      setError(err.message || 'Failed to create promo code');
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setCode('');
    setDescription('');
    setOrganization('EPL');
    setPromoType('');
    setPercentDiscount('');
    setFixedPricePerUnit('');
    setBuyQuantity('');
    setGetQuantity('');
    setGetDiscountPercent('');
    setUsageLimitPerCustomer('');
    setUsageLimitTotal('');
    setMinimumOrderQuantity('');
    setMinimumOrderValue('');
    setValidFrom('');
    setValidUntil('');
    setInternalNotes('');
    setError('');
  };
  
  const handleClose = () => {
    resetForm();
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Promo Code</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* Basic Info */}
        <TextField
          fullWidth
          label="Promo Code *"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          helperText="Uppercase letters, numbers, underscore, dash only (e.g., CASE25)"
          sx={{ mb: 2, mt: 1 }}
        />
        
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText="Internal description for this promo"
          sx={{ mb: 2 }}
        />
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Organization *</InputLabel>
          <Select
            value={organization}
            label="Organization *"
            onChange={(e) => setOrganization(e.target.value)}
          >
            <MenuItem value="BARE">BARE</MenuItem>
            <MenuItem value="EPL">EPL</MenuItem>
          </Select>
        </FormControl>
        
        {/* Promo Type Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Promo Type *</InputLabel>
          <Select
            value={promoType}
            label="Promo Type *"
            onChange={(e) => setPromoType(e.target.value)}
          >
            <MenuItem value="PERCENTAGE_DISCOUNT">Percentage Discount (e.g., 30% off)</MenuItem>
            <MenuItem value="FIXED_PRICE">Fixed Price Per Unit (e.g., $350/vial)</MenuItem>
            <MenuItem value="BUY_X_GET_Y_FREE">Buy X Get Y Free (e.g., Buy 2 Get 1 Free)</MenuItem>
            <MenuItem value="BUY_X_GET_Y_DISCOUNTED">Buy X Get Y Discounted (e.g., Buy 3 Get 2 at 50% off)</MenuItem>
          </Select>
        </FormControl>
        
        {/* Conditional Fields Based on Promo Type */}
        {promoType === 'PERCENTAGE_DISCOUNT' && (
          <TextField
            fullWidth
            type="number"
            label="Discount Percentage *"
            value={percentDiscount}
            onChange={(e) => setPercentDiscount(e.target.value)}
            InputProps={{ 
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            helperText="Enter percentage (e.g., 30 for 30% off)"
            sx={{ mb: 2 }}
          />
        )}
        
        {promoType === 'FIXED_PRICE' && (
          <TextField
            fullWidth
            type="number"
            label="Fixed Price Per Unit *"
            value={fixedPricePerUnit}
            onChange={(e) => setFixedPricePerUnit(e.target.value)}
            InputProps={{ 
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            helperText="Price per unit with this code (e.g., 350 for $350/vial)"
            sx={{ mb: 2 }}
          />
        )}
        
        {(promoType === 'BUY_X_GET_Y_FREE' || promoType === 'BUY_X_GET_Y_DISCOUNTED') && (
          <>
            <TextField
              fullWidth
              type="number"
              label="Buy Quantity *"
              value={buyQuantity}
              onChange={(e) => setBuyQuantity(e.target.value)}
              helperText="Number of items customer must buy"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              type="number"
              label="Get Quantity *"
              value={getQuantity}
              onChange={(e) => setGetQuantity(e.target.value)}
              helperText="Number of additional items customer gets"
              sx={{ mb: 2 }}
            />
          </>
        )}
        
        {promoType === 'BUY_X_GET_Y_DISCOUNTED' && (
          <TextField
            fullWidth
            type="number"
            label="Discount Percentage for Additional Items *"
            value={getDiscountPercent}
            onChange={(e) => setGetDiscountPercent(e.target.value)}
            InputProps={{ 
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            helperText="Discount on the 'get' items (e.g., 50 for 50% off)"
            sx={{ mb: 2 }}
          />
        )}
        
        {/* Usage Limits */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Usage Limits</Typography>
        </Divider>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Usage Limit Per Customer"
              value={usageLimitPerCustomer}
              onChange={(e) => setUsageLimitPerCustomer(e.target.value)}
              helperText="Leave empty for unlimited"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Total Usage Limit"
              value={usageLimitTotal}
              onChange={(e) => setUsageLimitTotal(e.target.value)}
              helperText="Total times code can be used"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        
        {/* Minimum Requirements */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Minimum Requirements</Typography>
        </Divider>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Order Quantity"
              value={minimumOrderQuantity}
              onChange={(e) => setMinimumOrderQuantity(e.target.value)}
              helperText="Minimum units required"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Order Value"
              value={minimumOrderValue}
              onChange={(e) => setMinimumOrderValue(e.target.value)}
              InputProps={{ 
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              helperText="Minimum dollar amount"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        
        {/* Validity Period */}
        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">Validity Period</Typography>
        </Divider>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Valid From"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for immediate"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Valid Until"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty to never expire"
              sx={{ mb: 2 }}
            />
          </Grid>
        </Grid>
        
        {/* Internal Notes */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Internal Notes"
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          helperText="Private notes for admin use only"
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Promo Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePromoCodeDialog;

